(function () {
  'use strict';

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  if ('IntersectionObserver' in window) {
    const animateObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in-view');
            animateObserver.unobserve(e.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0 }
    );

    const observeAnimated = (root = document) => {
      root.querySelectorAll('[data-animate-in]:not(.is-in-view)').forEach((el) => {
        animateObserver.observe(el);
      });
    };

    document.addEventListener('DOMContentLoaded', () => observeAnimated());

    document.addEventListener('shopify:section:load', (e) => observeAnimated(e.target));
  }

  function handleFirstTab(e) {
    if (e.key === 'Tab') {
      document.body.classList.add('user-is-tabbing');
      window.removeEventListener('keydown', handleFirstTab);
    }
  }
  window.addEventListener('keydown', handleFirstTab);

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let productCardObserver = null;

  function initProductCards(root = document) {
    const cards = root.querySelectorAll('.fs-product-card:not([data-fs-card-ready])');

    if (!productCardObserver && 'IntersectionObserver' in window) {
      productCardObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          productCardObserver.unobserve(entry.target);
        });
      }, { threshold: 0.14 });
    }

    cards.forEach((card, index) => {
      card.dataset.fsCardReady = 'true';
      card.style.transitionDelay = `${Math.min(index * 45, 180)}ms`;

      if (productCardObserver) {
        productCardObserver.observe(card);
      } else {
        card.classList.add('is-visible');
      }

      if (!reduceMotion.matches) {
        card.addEventListener('pointermove', (event) => {
          const rect = card.getBoundingClientRect();
          const x = ((event.clientX - rect.left) / rect.width - 0.5) * 8;
          const y = ((event.clientY - rect.top) / rect.height - 0.5) * 8;

          card.style.setProperty('--_card-move-x', `${x.toFixed(2)}px`);
          card.style.setProperty('--_card-move-y', `${y.toFixed(2)}px`);
        });

        card.addEventListener('pointerleave', () => {
          card.style.removeProperty('--_card-move-x');
          card.style.removeProperty('--_card-move-y');
        });
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => initProductCards());
  document.addEventListener('shopify:section:load', (e) => initProductCards(e.target));
  window.FsInitProductCards = initProductCards;
  initProductCards();

  window.FsEvents = window.FsEvents || {
    _listeners: new Map(),
    on(event, fn) {
      if (!this._listeners.has(event)) this._listeners.set(event, new Set());
      this._listeners.get(event).add(fn);
      return () => this._listeners.get(event).delete(fn);
    },
    emit(event, detail) {
      (this._listeners.get(event) || []).forEach((fn) => {
        try { fn(detail); } catch (err) { console.error('[fs-events]', err); }
      });
    },
  };

  window.FsCart = {
    async get() {
      const r = await fetch(window.Shopify.routes.root + 'cart.js', {
        headers: { 'Accept': 'application/json' },
      });
      if (!r.ok) throw new Error('Cart fetch failed');
      return r.json();
    },
    async add(items) {
      const r = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: Array.isArray(items) ? items : [items] }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.description || 'Add to cart failed');
      }
      const data = await r.json();
      window.FsEvents.emit('cart:updated');
      return data;
    },
    async change(payload) {
      const r = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error('Cart change failed');
      const data = await r.json();
      window.FsEvents.emit('cart:updated');
      return data;
    },
  };

  function ensureToastRegion() {
    let region = document.querySelector('[data-fs-toast-region]');
    if (region) return region;

    region = document.createElement('div');
    region.className = 'fs-toast-region';
    region.setAttribute('data-fs-toast-region', '');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    document.body.appendChild(region);
    return region;
  }

  window.FsToast = window.FsToast || {
    show(message, options = {}) {
      if (!message) return;
      const region = ensureToastRegion();
      const toast = document.createElement('div');
      toast.className = `fs-toast${options.tone === 'error' ? ' fs-toast--error' : ''}`;
      toast.textContent = message;
      region.appendChild(toast);

      window.requestAnimationFrame(() => toast.classList.add('is-visible'));
      window.setTimeout(() => {
        toast.classList.remove('is-visible');
        toast.addEventListener('transitionend', () => toast.remove(), { once: true });
      }, options.duration || 3400);
    },
  };

  document.addEventListener('click', (event) => {
    const stepButton = event.target.closest('[data-qty-step]');
    if (!stepButton) return;

    const control = stepButton.closest('[data-qty-control], .fs-main-product__qty-control');
    const input = control ? control.querySelector('input[type="number"]') : null;
    if (!input) return;

    const min = Number(input.getAttribute('min') || 0);
    const max = input.hasAttribute('max') ? Number(input.getAttribute('max')) : Infinity;
    const step = stepButton.dataset.qtyStep === 'minus' ? -1 : 1;
    const current = Number(input.value || min || 0);
    const next = Math.max(min, Math.min(max, current + step));

    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form || form.dataset.ajaxDisabled === 'true') return;

    event.preventDefault();
    const formData = new FormData(form);
    const id = formData.get('id');
    const quantity = Math.max(1, Number(formData.get('quantity') || 1));
    const button = form.querySelector('[name="add"], button[type="submit"]');
    const message = form.querySelector('[data-product-form-message]');
    const originalText = button ? button.textContent : '';
    const originalDisabled = button ? button.disabled : false;

    if (!id) {
      if (message) {
        message.textContent = 'Seleziona una variante disponibile.';
        message.hidden = false;
      }
      window.FsToast.show('Seleziona una variante disponibile.', { tone: 'error' });
      return;
    }

    try {
      if (button) {
        button.disabled = true;
        button.classList.add('is-loading');
      }

      await window.FsCart.add({ id, quantity });

      if (message) {
        message.textContent = 'Aggiunto al carrello.';
        message.hidden = false;
      }
      window.FsToast.show('Aggiunto al carrello.');
      const quickView = document.querySelector('fs-quick-view:not([hidden])');
      if (quickView && typeof quickView.close === 'function') quickView.close();
      if (window.FsEvents) window.FsEvents.emit('drawer:open', { name: 'cart' });
    } catch (error) {
      const errorMessage = error.message || 'Non sono riuscito ad aggiungere il prodotto.';
      if (message) {
        message.textContent = errorMessage;
        message.hidden = false;
      }
      window.FsToast.show(errorMessage, { tone: 'error' });
    } finally {
      if (button) {
        button.disabled = originalDisabled;
        button.classList.remove('is-loading');
        if (originalText) button.textContent = originalText;
      }
    }
  });

})();
